/**
 * VoiceAnalyticsCard — Admin Analytics tab section showing voice adoption
 * + per-caregiver leaderboard + kind/language breakdown.
 *
 * Three endpoints power this card:
 *   GET /api/admin/voice-analytics/summary?days=N
 *   GET /api/admin/voice-analytics/leaderboard?days=N
 *   GET /api/admin/voice-analytics/breakdown?days=N
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mic, Loader2, TrendingUp, Globe, Crown } from "lucide-react";

const RANGES = [
  { key: 7, label: "7d" },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
];

const KIND_LABEL = {
  shift: "Shift report",
  intake: "Client intake",
  incident: "Incident",
  mar: "MAR",
  family_update: "Family update",
  admin_command: "Admin command",
  incident_review: "Incident review",
  care_plan: "Care plan",
  caregiver_note: "Caregiver note",
  schedule: "Schedule",
  invoice_memo: "Invoice memo",
  applicant_note: "Applicant note",
  handoff: "Shift handoff",
};

const LANG_LABEL = {
  en: "English", es: "Spanish", fr: "French", tl: "Tagalog", sw: "Swahili",
  ht: "Haitian Cr.", pt: "Portuguese", zh: "Chinese", hi: "Hindi", ar: "Arabic",
  ru: "Russian", vi: "Vietnamese", ko: "Korean", de: "German", it: "Italian",
  auto: "Auto-detect",
};

export default function VoiceAnalyticsCard() {
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/admin/voice-analytics/summary?days=${days}`),
      api.get(`/admin/voice-analytics/leaderboard?days=${days}`),
      api.get(`/admin/voice-analytics/breakdown?days=${days}`),
    ])
      .then(([s, l, b]) => {
        if (!alive) return;
        setSummary(s.data);
        setLeaderboard(l.data || []);
        setBreakdown(b.data);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  return (
    <div className="card-surface p-5" data-testid="voice-analytics-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-purple-100 text-purple-700">
            <Mic className="h-4 w-4" />
          </span>
          <h2 className="font-heading text-base font-semibold text-clinical-text">
            Voice adoption
          </h2>
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-clinical-bg/60 p-0.5"
             data-testid="voice-analytics-range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setDays(r.key)}
              className={`px-2.5 py-1 text-xs rounded-md transition ${
                days === r.key ? "bg-white text-clinical-text font-medium shadow-sm"
                              : "text-clinical-subtext hover:text-clinical-text"
              }`}
              data-testid={`voice-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading || !summary ? (
        <div className="flex items-center gap-2 text-sm text-clinical-subtext py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading voice analytics…
        </div>
      ) : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <KPI label="Voice events" value={summary.total_events}
              delta={summary.delta_pct}
              testid="voice-kpi-events" />
            <KPI label="Active users" value={summary.active_users}
              sublabel={`of ${summary.total_eligible_users}`}
              testid="voice-kpi-active" />
            <KPI label="Adoption" value={`${summary.adoption_pct}%`}
              accent="#7c3aed"
              testid="voice-kpi-adoption" />
            <KPI label="Avg confidence" value={summary.avg_confidence != null ? summary.avg_confidence : "—"}
              sublabel={`${summary.failed} failed`}
              testid="voice-kpi-confidence" />
          </div>

          {/* Leaderboard */}
          <div className="mb-4">
            <div className="flex items-center gap-1.5 mb-2">
              <Crown className="h-3.5 w-3.5 text-amber-500" />
              <div className="label-eyebrow">Top voice users</div>
            </div>
            {leaderboard.length === 0 ? (
              <p className="text-xs text-clinical-subtext italic py-2">
                No voice usage in this window yet. Caregivers can start by tapping
                "Voice fill" on a shift report.
              </p>
            ) : (
              <div className="space-y-1.5">
                {leaderboard.slice(0, 8).map((u, idx) => (
                  <div
                    key={u.user_id}
                    className="flex items-center gap-3 rounded-lg border border-clinical-border/60 bg-white px-3 py-2"
                    data-testid={`voice-leaderboard-row-${idx}`}
                  >
                    <span className="w-5 text-center text-xs font-mono text-clinical-subtext">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-clinical-text truncate">
                        {u.user_name}
                      </div>
                      <div className="text-[10px] text-clinical-subtext">
                        {u.user_role} · {u.top_kinds.slice(0, 3).map((k) => KIND_LABEL[k] || k).join(" · ")}
                      </div>
                    </div>
                    {u.languages.length > 0 && (
                      <div className="hidden sm:flex items-center gap-1 text-[10px] text-clinical-subtext">
                        <Globe className="h-3 w-3" />
                        {u.languages.slice(0, 3).join(", ")}
                      </div>
                    )}
                    {u.avg_confidence != null && (
                      <span className="pill !text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {u.avg_confidence.toFixed(2)}
                      </span>
                    )}
                    <span className="text-sm font-mono tabular-nums text-clinical-text w-8 text-right">
                      {u.total_events}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Kind + language breakdowns */}
          {breakdown && (Object.keys(breakdown.by_kind).length > 0 || Object.keys(breakdown.by_language).length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-clinical-border">
              <Distribution
                title="By voice surface"
                data={breakdown.by_kind}
                labelMap={KIND_LABEL}
                testid="voice-dist-kind"
              />
              <Distribution
                title="By language"
                data={breakdown.by_language}
                labelMap={LANG_LABEL}
                testid="voice-dist-language"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KPI({ label, value, sublabel, delta, accent = "#0891B2", testid }) {
  const deltaSign = delta != null && delta > 0 ? "+" : "";
  const deltaColor = delta == null ? "text-clinical-subtext"
                    : delta > 0 ? "text-emerald-600"
                    : delta < 0 ? "text-rose-600"
                    : "text-clinical-subtext";
  return (
    <div className="rounded-xl border border-clinical-border/60 bg-clinical-bg/40 p-3"
         data-testid={testid}>
      <div className="text-[10px] tracking-[0.12em] uppercase text-clinical-subtext font-semibold mb-1">
        {label}
      </div>
      <div className="flex items-baseline gap-1.5">
        <div className="font-heading text-xl font-semibold" style={{ color: accent }}>
          {value}
        </div>
        {delta != null && (
          <div className={`text-[10px] font-medium flex items-center gap-0.5 ${deltaColor}`}>
            <TrendingUp className="h-2.5 w-2.5" />
            {deltaSign}{delta}%
          </div>
        )}
      </div>
      {sublabel && <div className="text-[10px] text-clinical-subtext mt-0.5">{sublabel}</div>}
    </div>
  );
}

function Distribution({ title, data, labelMap, testid }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const max = entries.reduce((m, [, v]) => Math.max(m, v), 0) || 1;
  return (
    <div data-testid={testid}>
      <div className="label-eyebrow mb-2">{title}</div>
      <div className="space-y-1.5">
        {entries.map(([key, count]) => (
          <div key={key} className="flex items-center gap-2">
            <span className="text-xs text-clinical-text w-24 truncate">{labelMap[key] || key}</span>
            <div className="flex-1 h-1.5 rounded-full bg-clinical-bg overflow-hidden">
              <div className="h-full bg-purple-500"
                style={{ width: `${(count / max) * 100}%` }} />
            </div>
            <span className="text-xs font-mono tabular-nums text-clinical-subtext w-6 text-right">
              {count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
